import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wrench, ShieldAlert, Inbox, ClipboardList, IndianRupee, TrendingUp,
  Phone, Clock, CheckCircle2, X, Plus, Trash2, Camera,
} from "lucide-react";

const STATUS_TONE = {
  "Workshop notified": "bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300",
  "Quote sent":        "bg-blue-100 dark:bg-blue-700/15 text-blue-800 dark:text-blue-300",
  "Repair in progress":"bg-orange-100 dark:bg-orange-500/15 text-orange-800 dark:text-orange-300",
  "Repair completed":  "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
  "Closed":            "bg-slate-100 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300",
};

export default function WorkshopPortal() {
  const { user } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ open: 0, quote_sent: 0, completed: 0, weekly: 0, total_revenue_inr: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [inbox, s] = await Promise.all([
        api.get("/partners/workshop/quote-requests"),
        api.get("/partners/workshop/stats"),
      ]);
      setRequests(inbox.data);
      setStats(s.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load workshop data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const promoteToWorkshop = async () => {
    try {
      await api.post("/dev/set-role/workshop");
      toast.success("Role set to workshop. Refreshing...");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed. Enable dev endpoints in backend.");
    }
  };

  if (user && !["workshop", "admin"].includes(user.role)) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 mesh-subtle" data-testid="workshop-no-access">
        <div className="glass-strong rounded-2xl p-8 text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">Workshop access required</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            Your account is <span className="font-semibold">{user.role}</span>. This portal is for our partner workshops.
          </p>
          <button
            onClick={promoteToWorkshop}
            className="mt-5 btn-signature rounded-lg px-5 py-2.5 font-semibold text-sm"
            data-testid="workshop-demo-promote"
          >
            Try demo as workshop
          </button>
          <p className="text-[10px] text-slate-500 mt-2">(dev-only role switch)</p>
        </div>
      </div>
    );
  }

  const filtered = tab === "inbox"
    ? requests.filter(r => r.status === "Workshop notified")
    : tab === "active"
    ? requests.filter(r => ["Quote sent", "Repair in progress"].includes(r.status))
    : tab === "completed"
    ? requests.filter(r => ["Repair completed", "Closed"].includes(r.status))
    : requests;

  return (
    <div className="min-h-[calc(100vh-4rem)] mesh-subtle py-10" data-testid="workshop-portal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-400 mb-2 flex items-center gap-2">
              <Wrench className="w-3 h-3" /> Workshop Portal
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Your repair operations, in one view.
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Manage quote requests, submit estimates, and update repair status.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Stat label="Open requests" value={stats.open} icon={Inbox} tone="from-amber-500 to-amber-700" testid="ws-stat-open" />
          <Stat label="Quotes sent" value={stats.quote_sent} icon={ClipboardList} tone="from-blue-700 to-blue-900" testid="ws-stat-quote-sent" />
          <Stat label="Completed" value={stats.completed} icon={CheckCircle2} tone="from-emerald-500 to-emerald-700" testid="ws-stat-completed" />
          <Stat label="This week" value={stats.weekly} icon={TrendingUp} tone="from-orange-500 to-orange-600" testid="ws-stat-weekly" />
          <Stat label="Revenue booked" value={`₹${(stats.total_revenue_inr || 0).toLocaleString("en-IN")}`} icon={IndianRupee} tone="from-pink-500 to-pink-700" testid="ws-stat-revenue" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1 w-fit">
          {[
            { k: "inbox", label: "Inbox", count: requests.filter(r => r.status === "Workshop notified").length },
            { k: "active", label: "Active repairs", count: requests.filter(r => ["Quote sent", "Repair in progress"].includes(r.status)).length },
            { k: "completed", label: "Completed", count: requests.filter(r => ["Repair completed", "Closed"].includes(r.status)).length },
            { k: "all", label: "All", count: requests.length },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                tab === t.k
                  ? "bg-blue-800 text-white shadow-md"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
              data-testid={`ws-tab-${t.k}`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-white/20" : "bg-slate-200 dark:bg-slate-700"}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {error && <div className="p-6 text-center text-rose-600 dark:text-rose-400 text-sm">{error}</div>}
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-14 text-center" data-testid="workshop-empty">
              <Inbox className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-700 dark:text-slate-300 font-semibold">Nothing here yet</p>
              <p className="text-sm text-slate-500 mt-1">New requests will appear here in real time.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((q, i) => (
                <motion.li
                  key={q.quote_request_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-center justify-between gap-4 cursor-pointer"
                  onClick={() => setSelected(q)}
                  data-testid={`ws-qr-${q.quote_request_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-sm font-bold text-slate-900 dark:text-white">{q.quote_request_id}</div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[q.status] || "bg-slate-100 text-slate-700"}`}>
                        {q.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {q.name || "Anonymous"} · <Phone className="w-3 h-3 inline" /> {q.mobile} · {q.workshop_city}
                    </div>
                    {q.notes && <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-1">📝 {q.notes}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    {q.estimate?.total_inr ? (
                      <div className="font-heading font-bold text-lg text-blue-800 dark:text-blue-400">₹{q.estimate.total_inr.toLocaleString("en-IN")}</div>
                    ) : (
                      <div className="text-xs text-slate-400">No estimate yet</div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {new Date(q.created_at).toLocaleDateString("en-IN")}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && (
        <QuoteRequestDrawer
          qr={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="card-premium p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-heading font-extrabold text-2xl text-slate-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}

function QuoteRequestDrawer({ qr, onClose, onSaved }) {
  const [items, setItems] = useState(qr.estimate?.line_items || [{ part: "", qty: 1, price: 0 }]);
  const [labourCost, setLabourCost] = useState(qr.estimate?.labour_cost || 0);
  const [days, setDays] = useState(qr.estimate?.estimated_days || 3);
  const [notes, setNotes] = useState(qr.estimate?.notes || "");
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const total = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 1), 0) + (Number(labourCost) || 0);

  const submitEstimate = async () => {
    if (!items.some((it) => it.part && it.price)) {
      toast.error("Add at least one part with a price");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/partners/workshop/quote-requests/${qr.quote_request_id}/estimate`, {
        line_items: items.filter((it) => it.part),
        labour_cost: Number(labourCost) || 0,
        estimated_days: Number(days) || 3,
        notes,
      });
      toast.success("Estimate submitted. Customer notified.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    setStatusSaving(true);
    try {
      await api.post(`/partners/workshop/quote-requests/${qr.quote_request_id}/status`, { status });
      toast.success(`Status → ${status}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="workshop-drawer">
      <div className="flex-1 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="w-full max-w-xl bg-white dark:bg-slate-950 shadow-2xl overflow-y-auto border-l border-slate-200 dark:border-slate-800"
      >
        <div className="sticky top-0 bg-white/95 dark:bg-slate-950/95 backdrop-blur px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Request</div>
            <div className="font-mono font-bold text-slate-900 dark:text-white">{qr.quote_request_id}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center" data-testid="workshop-drawer-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Customer</div>
            <div className="text-slate-900 dark:text-white font-semibold">{qr.name || "Anonymous"}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2 mt-1">
              <Phone className="w-3.5 h-3.5" /> <a href={`tel:${qr.mobile}`} className="hover:underline">{qr.mobile}</a>
            </div>
            {qr.notes && <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">📝 {qr.notes}</div>}
            {qr.doc_ids?.length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <Camera className="w-3 h-3" /> {qr.doc_ids.length} photo(s) attached
              </div>
            )}
          </div>

          {/* Estimate builder */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-slate-900 dark:text-white">Repair estimate</h3>
              <button
                onClick={() => setItems([...items, { part: "", qty: 1, price: 0 }])}
                className="text-xs font-semibold text-blue-800 hover:text-blue-900 flex items-center gap-1"
                data-testid="ws-add-line-item"
              >
                <Plus className="w-3 h-3" /> Add line item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    className="col-span-6 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
                    placeholder="Part / Service"
                    value={it.part}
                    onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, part: e.target.value } : x))}
                    data-testid={`ws-line-part-${idx}`}
                  />
                  <input
                    type="number"
                    min="1"
                    className="col-span-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
                    placeholder="Qty"
                    value={it.qty}
                    onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))}
                    data-testid={`ws-line-qty-${idx}`}
                  />
                  <input
                    type="number"
                    min="0"
                    className="col-span-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
                    placeholder="₹ price"
                    value={it.price}
                    onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, price: e.target.value } : x))}
                    data-testid={`ws-line-price-${idx}`}
                  />
                  <button
                    onClick={() => setItems(items.filter((_, i) => i !== idx))}
                    className="col-span-1 text-slate-400 hover:text-rose-500 flex items-center justify-center"
                    aria-label="Remove"
                    data-testid={`ws-line-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Labour cost (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={labourCost}
                  onChange={(e) => setLabourCost(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
                  data-testid="ws-labour-cost"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Estimated days</label>
                <input
                  type="number"
                  min="1"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
                  data-testid="ws-estimate-days"
                />
              </div>
            </div>

            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes for the customer (e.g. OEM vs aftermarket parts)"
              className="mt-3 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-700 focus:outline-none"
              data-testid="ws-estimate-notes"
            />

            <div className="mt-4 rounded-xl bg-gradient-to-r from-blue-800 to-blue-950 text-white p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Estimate total</div>
                <div className="font-heading font-black text-3xl">₹{total.toLocaleString("en-IN")}</div>
              </div>
              <button
                onClick={submitEstimate}
                disabled={saving}
                className="bg-white text-blue-900 font-bold rounded-lg px-5 py-2.5 hover:bg-white/90 disabled:opacity-60"
                data-testid="ws-submit-estimate"
              >
                {saving ? "Sending..." : "Send to customer"}
              </button>
            </div>
          </div>

          {/* Status controls */}
          <div>
            <h3 className="font-heading font-bold text-slate-900 dark:text-white mb-3">Update status</h3>
            <div className="flex flex-wrap gap-2">
              {["Repair in progress", "Repair completed", "Closed"].map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  disabled={statusSaving}
                  className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-700 text-sm font-semibold text-slate-700 dark:text-slate-200 disabled:opacity-60"
                  data-testid={`ws-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.aside>
    </div>
  );
}
